import React, { useState, useMemo, useEffect } from 'react'
import styled from 'styled-components'
import { padding, PaddingProps } from 'styled-system'
import { useTranslation } from 'react-i18next'
import { useFormikContext } from 'formik'

import { Dropdown, ErrorMessage, TextButton, TextInput, DropdownItemType } from 'components'

import { LeadDetails } from 'types/global'
import { WidthProps } from 'types/styled'
import useWorkFlowLookup from 'hooks/lookup/use-workflow-lookup'

import EmailAddressGroup from './EmailAddressGroup'
import MobileNumberGroup from './MobileNumberGroup'
import { DUP_CHECK_ERRORS } from 'modules/Leads/NewLead/constants'

type Props = PaddingProps & {
  disabled?: boolean
  inline?: boolean
  name: keyof LeadDetails.LeadDetailsForm
  displayName?: string
  fieldName?: string
  fromNewLeadPage?: boolean
  withMarkAsPrimary?: boolean
  width?: WidthProps
}

const ContactSection: React.FC<Props> = ({
  name,
  fromNewLeadPage = true,
  padding,
  paddingLeft,
  width = 'auto',
  ...props
}) => {
  const { t } = useTranslation()
  const { getFieldProps, values, errors, isValidating, setFieldValue } = useFormikContext<LeadDetails.LeadDetailsForm>()

  const [nameFieldError, setNameFieldError] = useState<string>()
  const hasJobtitle = !!values?.contactPerson.jobTitle || !!values?.contactPerson?.contactTypeId
  const [showMore, setShowMore] = useState<boolean>(!!props.disabled || hasJobtitle)

  const { contactTypes, isSuccess } = useWorkFlowLookup({})

  const { contactTypeOptions, contactTypeValue } = useMemo(() => {
    if (!isSuccess) {
      return { contactTypeOptions: [], contactTypeValue: undefined }
    }

    const options = contactTypes.map(({ id, name }) => ({ value: id, label: name }))
    const contactType = options.find((i) => i.value === values?.contactPerson?.contactTypeId) || null
    return {
      contactTypeOptions: options,
      contactTypeValue: contactType,
    }
  }, [contactTypes, isSuccess, values?.contactPerson?.contactTypeId])

  const nameInputProps = {
    disabled: props.disabled,
    inline: true,
    width: 180,
  }

  const containerProps = {
    padding: { padding, paddingLeft },
    width,
  }

  const resolveNameFieldError = () => {
    const errorMessage = errors?.contactPerson?.fname ?? errors?.contactPerson?.lname ?? undefined
    const errorVariant = nameFieldError === DUP_CHECK_ERRORS.POTENTIAL_DUP_NAME ? 'warning' : 'error'
    return errorMessage ? (
      <ErrorMessage>{errorMessage}</ErrorMessage>
    ) : nameFieldError ? (
      <ErrorMessage errorVariant={errorVariant}>{nameFieldError}</ErrorMessage>
    ) : undefined
  }

  useEffect(() => {
    if (errors.contactPerson?.fname === DUP_CHECK_ERRORS.EXACT_DUP_NAME && !nameFieldError) {
      setNameFieldError(DUP_CHECK_ERRORS.EXACT_DUP_NAME)
    }
  }, [errors, name, nameFieldError])

  const jobTitleContactType = useMemo(
    () => (
      <MoreFieldsContainer>
        <TextInput
          disabled={props.disabled}
          inline
          name={`${name}.jobTitle`}
          label="job_title_lbl"
          mb={16}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue(`${name}.jobTitle`, e.target.value)}
          value={values?.contactPerson.jobTitle}
          error={errors.contactPerson?.jobTitle}
          variant="labeled"
        />
        <Dropdown
          marginTop="18px"
          error={errors.contactPerson?.contactTypeId}
          label="contact_type_lbl"
          isDisabled={props.disabled}
          name={`${name}.contactTypeId`}
          options={contactTypeOptions}
          onChange={(opt: DropdownItemType<string>) => setFieldValue(`${name}.contactTypeId`, opt.value)}
          value={contactTypeValue}
          variant="labeled"
        />
      </MoreFieldsContainer>
    ),
    [contactTypeOptions, contactTypeValue, errors.contactPerson, name, props.disabled, setFieldValue, values],
  )

  return (
    <>
      <FieldsContainer {...containerProps}>
        <SectionContainer>
          <LabelText disabled={props.disabled}>{t('name_lbl')}</LabelText>
          <NameContainer>
            <TextInput
              {...nameInputProps}
              placeholder="first_name_lbl"
              variant="boxless"
              {...getFieldProps(`${name}.fname`)}
            />
            <TextInput
              {...nameInputProps}
              placeholder="last_name_lbl"
              variant="boxless"
              {...getFieldProps(`${name}.lname`)}
            />
          </NameContainer>
          {resolveNameFieldError()}
        </SectionContainer>
        <SectionContainer>
          <EmailAddressGroup disabled={props.disabled} name={`${name}.email`} isLoading={isValidating} />
        </SectionContainer>
        <SectionContainer>
          <MobileNumberGroup disabled={props.disabled} name={`${name}.mobile`} isLoading={isValidating} />
        </SectionContainer>
        {fromNewLeadPage ? (
          showMore ? (
            jobTitleContactType
          ) : (
            <StyledTextButton
              icon="plus"
              inline
              onClick={() => {
                setShowMore(true)
              }}
            >
              add_job_title_contact_type_btn
            </StyledTextButton>
          )
        ) : (
          jobTitleContactType
        )}
      </FieldsContainer>
    </>
  )
}

const FieldsContainer = styled.div<{ padding: PaddingProps; width: WidthProps }>`
  ${padding}
  width: ${(p) => (p.width === 'auto' ? '100%' : `${p.width}px`)};
`

const NameContainer = styled.div<{ disabled?: boolean; error?: string }>`
  background-color: ${(p) => p.theme.colors.gray1};
  border: 1px solid ${(p) => (p.error ? p.theme.colors.red : p.theme.colors.defaultBorder)};
  border-radius: 4px;
  display: inline-flex;
  margin-bottom: 5px;
  width: 90%;

  input {
    border-radius: 4px;
    width: 100%;
    background-color: ${(p) => p.theme.colors.gray1};
  }

  &:focus-within {
    ${(p) =>
      !p.disabled &&
      `
      background-color: ${p.theme.colors.white};
      border-color: ${p.theme.colors.blue1};
      box-shadow: 0 0 0 4px rgba(143, 155, 179, 0.1);
      color: ${p.theme.colors.defaultText};
    `}
  }

  &:focus-within > input {
    ${(p) =>
      !p.disabled &&
      `
      background-color: ${p.theme.colors.white};
      border-color: ${p.theme.colors.blue1};
      box-shadow: 0 0 0 4px rgba(143, 155, 179, 0.1);
      color: ${p.theme.colors.defaultText};
    `}
  }

  & > div {
    width: 50%;
  }
`

const LabelText = styled.div<{ disabled?: boolean }>`
  ${(p) => p.theme.font({ color: p.disabled ? 'gray4' : 'defaultText' })}
  margin-bottom: 6px;
`

const SectionContainer = styled.div`
  margin: 16px 0;
`

const MoreFieldsContainer = styled.div`
  margin: 16px 0;
  display: flex;
  flex-direction: column;
`
const StyledTextButton = styled((p) => <TextButton {...p} />)`
  margin: 4px 0;
`

ContactSection.displayName = 'ContactSection'
export default ContactSection
